import type { Category } from "@/api/types";

/**
 * Дерево категорий консоли (дерево v2, миграция 134): до трёх уровней —
 * аудитория → тип → вид. Здесь чистая логика страницы «Категории»: порядок
 * строк, уровень, унаследованные раздел/пол/сетка, архив и то, куда узел
 * можно перенести, не нарушив глубину. Шлюз проверяет то же самое — консоль
 * просто не предлагает заведомо запрещённое.
 */
export const MAX_CATEGORY_DEPTH = 3;

export type CategoryTreeRow = {
	category: Category;
	/** 1 — корень. */
	depth: number;
	/** Узел или его предок в архиве. */
	archived: boolean;
	/** Есть ли неархивные дети. */
	hasChildren: boolean;
	/** Высота поддерева: 1 — лист. Считает и архивных детей — как шлюз при переносе. */
	height: number;
	/** Конечный узел: активных детей нет — сюда продавец кладёт товар. */
	isLeaf: boolean;
	/** Товаров во всей ветке (узел + потомки любой глубины, без архивных товаров). */
	branchProducts: number;
	branchPublished: number;
	/** Значения с учётом предков: откуда взято — `inheritedFrom` (id предка) или сам узел. */
	sizeGrid: { value: string | null; inheritedFrom: string | null };
	section: { value: string | null; inheritedFrom: string | null };
	gender: { value: string | null; inheritedFrom: string | null };
	path: string[];
};

export type CategoryTree = {
	/** Активное дерево в порядке витрины: узел, за ним его дети (любой глубины). */
	active: CategoryTreeRow[];
	/** Архивные узлы (с их поддеревом) — отдельным блоком. */
	archived: CategoryTreeRow[];
	byId: Map<string, CategoryTreeRow>;
	/** Строки, у которых родитель пропал из базы. */
	orphans: CategoryTreeRow[];
};

const sortSiblings = (a: Category, b: Category) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, "ru");

export function buildCategoryTree(items: Category[]): CategoryTree {
	const byCat = new Map(items.map((c) => [c.id, c]));
	const kids = new Map<string | null, Category[]>();
	for (const c of items) {
		const key = c.parentId && byCat.has(c.parentId) ? c.parentId : c.parentId === null ? null : "__orphan__";
		const list = kids.get(key) ?? [];
		list.push(c);
		kids.set(key, list);
	}
	for (const list of kids.values()) list.sort(sortSiblings);

	const byId = new Map<string, CategoryTreeRow>();
	const inherit = (
		chain: Category[],
		pick: (c: Category) => string | null | undefined,
	): { value: string | null; inheritedFrom: string | null } => {
		for (const [i, c] of chain.entries()) {
			const v = pick(c);
			if (v) return { value: v, inheritedFrom: i === 0 ? null : c.id };
		}
		return { value: null, inheritedFrom: null };
	};

	const heightOf = (id: string, guard = 0): number => {
		if (guard > 10) return 1;
		const children = kids.get(id) ?? [];
		return children.length ? 1 + Math.max(...children.map((c) => heightOf(c.id, guard + 1))) : 1;
	};

	const branchSum = (c: Category, pick: (x: Category) => number, guard = 0): number =>
		guard > 10 ? 0 : pick(c) + (kids.get(c.id) ?? []).reduce((sum, x) => sum + branchSum(x, pick, guard + 1), 0);

	const visit = (c: Category, ancestors: Category[], out: CategoryTreeRow[], guard = 0) => {
		if (guard > 10 || byId.has(c.id)) return;
		const chain = [c, ...ancestors];
		const row: CategoryTreeRow = {
			category: c,
			depth: chain.length,
			archived: chain.some((x) => x.archived),
			hasChildren: (kids.get(c.id) ?? []).some((x) => !x.archived),
			height: heightOf(c.id),
			isLeaf: !(kids.get(c.id) ?? []).some((x) => !x.archived),
			branchProducts: branchSum(c, (x) => x.productCount ?? 0),
			branchPublished: branchSum(c, (x) => x.publishedCount ?? 0),
			sizeGrid: inherit(chain, (x) => x.sizeGrid),
			section: inherit(chain, (x) => x.section),
			gender: inherit(chain, (x) => x.gender),
			path: chain.map((x) => x.name).reverse(),
		};
		byId.set(c.id, row);
		out.push(row);
		for (const child of kids.get(c.id) ?? []) visit(child, chain, out, guard + 1);
	};

	const all: CategoryTreeRow[] = [];
	for (const root of kids.get(null) ?? []) visit(root, [], all);
	const orphans: CategoryTreeRow[] = [];
	for (const o of kids.get("__orphan__") ?? []) visit(o, [], orphans);

	return {
		active: all.filter((r) => !r.archived),
		// В архивном блоке — только «вершины» архива: поддерево показывается под ними.
		archived: all.filter((r) => r.archived),
		byId,
		orphans,
	};
}

/**
 * Куда можно перенести узел: не в себя и не в свою ветку, не в архив и так,
 * чтобы уровень родителя + высота переносимой ветки не превысили трёх.
 * Для нового узла (`selfId` нет) — любой неархивный узел не глубже второго уровня.
 */
export function allowedParents(tree: CategoryTree, selfId?: string): CategoryTreeRow[] {
	const self = selfId ? tree.byId.get(selfId) : undefined;
	const height = self?.height ?? 1;
	return tree.active.filter((row) => {
		if (self) {
			if (row.category.id === self.category.id) return false;
			// Своя ветка: путь кандидата проходит через переносимый узел.
			let cur: CategoryTreeRow | undefined = row;
			while (cur) {
				if (cur.category.id === self.category.id) return false;
				cur = cur.category.parentId ? tree.byId.get(cur.category.parentId) : undefined;
			}
		}
		return row.depth + height <= MAX_CATEGORY_DEPTH;
	});
}

/** Узел и все его потомки любой глубины — «узел включает ветку» для фильтров. */
export function subtreeIds(items: Pick<Category, "id" | "parentId">[], id: string): Set<string> {
	const out = new Set<string>([id]);
	let grew = true;
	while (grew) {
		grew = false;
		for (const c of items) {
			if (c.parentId && out.has(c.parentId) && !out.has(c.id)) {
				out.add(c.id);
				grew = true;
			}
		}
	}
	return out;
}

/** Куда можно положить товар: конечные неархивные узлы в порядке дерева. */
export function leafTargets(tree: CategoryTree): CategoryTreeRow[] {
	return tree.active.filter((row) => row.isLeaf);
}

/**
 * Характеристики узла с наследованием — как их берёт форма товара на шлюзе:
 * своя непустая схема узла, иначе ближайшего предка.
 */
export function resolveAttributeSchema<T extends { attributes: unknown[] }>(
	tree: CategoryTree,
	id: string,
	own: (categoryId: string) => T | undefined,
): { from: CategoryTreeRow; schema: T } | null {
	for (let cur = tree.byId.get(id); cur; cur = cur.category.parentId ? tree.byId.get(cur.category.parentId) : undefined) {
		const schema = own(cur.category.id);
		if (schema && schema.attributes.length > 0) return { from: cur, schema };
	}
	return null;
}

const TRANSLIT: Record<string, string> = {
	а: "a", б: "b", в: "v", г: "g", д: "d", е: "e", ё: "e", ж: "zh", з: "z", и: "i", й: "y", к: "k", л: "l", м: "m",
	н: "n", о: "o", п: "p", р: "r", с: "s", т: "t", у: "u", ф: "f", х: "h", ц: "ts", ч: "ch", ш: "sh", щ: "sch",
	ъ: "", ы: "y", ь: "", э: "e", ю: "yu", я: "ya",
};

/**
 * Ключ характеристики из названия: «Материал верха» → `materialVerha`.
 * Ключ — то, под чем значение лежит в карточке товара, поэтому латиница без
 * пробелов; модератор может переписать его руками.
 */
export function attributeKeyFromLabel(label: string): string {
	const words = [...label.toLowerCase()]
		.map((ch) => TRANSLIT[ch] ?? ch)
		.join("")
		.replace(/[^a-z0-9]+/g, " ")
		.trim()
		.split(" ")
		.filter(Boolean);
	return words.map((w, i) => (i === 0 ? w : w[0]?.toUpperCase() + w.slice(1))).join("");
}

export type AttributeDraftProblem = { code: "emptyLabel" | "emptyKey" | "duplicateKey" | "noOptions"; key: string };

/** Те же отказы, что у шлюза (PUT /admin/categories/:id/attribute-schema), плюс пустые поля. */
export function validateAttributeDraft(
	attributes: { key: string; label: string; type: string; options?: string[] }[],
): AttributeDraftProblem | null {
	const seen = new Set<string>();
	for (const a of attributes) {
		if (!a.label.trim()) return { code: "emptyLabel", key: a.key };
		if (!a.key.trim()) return { code: "emptyKey", key: a.label };
		if (seen.has(a.key)) return { code: "duplicateKey", key: a.key };
		seen.add(a.key);
		if (a.type === "select" && !a.options?.length) return { code: "noOptions", key: a.key };
	}
	return null;
}

export type ProductCategoryOption = { id: string; label: string; count: number; depth: number; archived: boolean };

/**
 * Пункты фильтра категорий в «Товарах»: дерево в порядке витрины с отступом
 * по уровню, у каждого узла — число товаров всей ветки (по счётчикам шлюза
 * на конечных узлах). Пустые ветки не показываем; архивные — в конце с
 * пометкой; «без категории» — последним, если такие карточки есть.
 */
export function productCategoryOptions(
	categories: Category[],
	counts: { id: string; count: number }[],
	labels: { none: string; archived: string },
): ProductCategoryOption[] {
	const tree = buildCategoryTree(categories);
	const countOf = new Map(counts.map((c) => [c.id, c.count]));
	const out: ProductCategoryOption[] = [];
	const branchCount = (id: string) => {
		let sum = 0;
		for (const x of subtreeIds(categories, id)) sum += countOf.get(x) ?? 0;
		return sum;
	};
	for (const row of [...tree.active, ...tree.orphans]) {
		const count = branchCount(row.category.id);
		if (count > 0)
			out.push({
				id: row.category.id,
				label: `${"\u2003".repeat(row.depth - 1)}${row.category.name}`,
				count,
				depth: row.depth,
				archived: false,
			});
	}
	for (const row of tree.archived) {
		if (!row.category.archived) continue;
		const count = branchCount(row.category.id);
		if (count > 0)
			out.push({
				id: row.category.id,
				label: `${row.path.join(" / ")} · ${labels.archived}`,
				count,
				depth: row.depth,
				archived: true,
			});
	}
	const known = new Set(categories.map((c) => c.id));
	for (const c of counts) {
		if (c.id === "__none__" || known.has(c.id)) continue;
		out.push({ id: c.id, label: c.id, count: c.count, depth: 1, archived: false });
	}
	const none = countOf.get("__none__");
	if (none) out.push({ id: "__none__", label: labels.none, count: none, depth: 1, archived: false });
	return out;
}
